import { mlcUsageUrl, useUsage } from "@/services/reporting";
import { BarChart, Download } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { formatSeconds } from "@/utils";
import { useState } from "react";

export default function Usage() {
  const [dt, setDt] = useState(new Date());
  const year = dt.getFullYear();
  const month = dt.getMonth();
  const startDate = new Date(year, month, 1).toISOString().split("T")[0];
  const endDate = new Date(year, month + 1, 0).toISOString().split("T")[0];
  const { data, isLoading, error } = useUsage(startDate, endDate);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  const columns = [
    {
      field: "usage_id",
      headerName: "Usage Identifier",
      width: 120,
    },
    {
      field: "content_key",
      headerName: "DSP Recording Identifier",
      width: 100,
      valueFormatter: (value) => value?.replaceAll("-", ""),
    },
    {
      field: "recording_title",
      headerName: "Recording Title",
      width: 200,
    },
    {
      field: "recording_alternative_titles",
      headerName: "Recording Alternative Titles",
      width: 220,
    },
    {
      field: "recording_artist",
      headerName: "Recording Artist",
      width: 180,
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 100,
      type: "number",
      valueFormatter: (value) => formatSeconds(value),
    },
    {
      field: "date_first_utilized",
      headerName: "Recording Date First Utilized",
      width: 220,
    },
    {
      field: "label",
      headerName: "Label Name",
      width: 150,
    },
    {
      field: "writers",
      headerName: "Writers",
      width: 220,
    },
    {
      field: "territory_code",
      headerName: "Territory",
      width: 100,
    },
    {
      field: "usage_start_date",
      headerName: "Usage Start Date",
      width: 160,
    },
    {
      field: "usage_end_date",
      headerName: "Usage End Date",
      width: 160,
    },
    {
      field: "number_of_usages",
      headerName: "Original Number of Usages",
      width: 200,
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        alignItems={"center"}
        my={2}
        justifyContent={"space-between"}
      >
        <Stack direction="row" spacing={2} alignItems={"center"}>
          <BarChart sx={{ fontSize: 34 }} color="primary" />
          <Typography
            style={{
              fontFamily: "'Montserrat Alternates', sans-serif",
              fontWeight: 700,
              fontSize: "1.8rem",
              marginBottom: "2px",
            }}
            gutterBottom
          >
            usage
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems={"center"}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Download />}
            href={mlcUsageUrl(startDate, endDate)}
            download
          >
            MLC TSV
          </Button>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              views={["month", "year"]}
              value={dt}
              onChange={(newDate) => setDt(newDate)}
              format="MMM yyyy"
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Stack>

      <Stack direction={"row"} spacing={2} alignItems={"center"} mb={2}>
        <Box sx={{ height: "70vh", width: "100%" }}>
          <DataGrid
            rows={data ?? []}
            columns={columns}
            getRowId={(row) =>
              `${row.usage_id}-${row.usage_start_date}-${row.territory_code}`
            }
            showToolbar
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: `${Math.floor(Date.now() / 1000)}-notebook-usage-report-${startDate}-${endDate}`, // Custom filename with unix timestamp
                  delimiter: ",",
                  utf8WithBom: true,
                },
              },
            }}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: "usage_start_date",
                    sort: "desc",
                  },
                ],
              },
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
}
